import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Recycle, Package, CheckCircle2 } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";

// Recovery & Circularity — Sprint 2
// 6-step pipeline (HR exit → Collect → Wipe → Grade → Decide → HAM close)
// with per-device chain-of-custody trail and resell/recycle decision logic.

const STEP_ICONS = { hr_exit: Package, collect: Package, wipe: ShieldCheck, grade: CheckCircle2, decide: ArrowRight, ham_close: Recycle };

export default function Recovery() {
  const { brand } = useBranding();
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    endpoints.lifecycleRecoverySummary().then(setSummary).catch(() => {});
    endpoints.lifecycleRecoveryActive().then((d) => {
      setActive(d.active || []);
      if (d.active?.[0]) setSelectedId(d.active[0].id);
    }).catch(() => {});
  }, []);

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";
  const steps = summary?.steps || [];
  const selected = active.find((r) => r.id === selectedId);

  const decisionColor = {
    Resell: "#0F766E",
    Redeploy: "#1E4FD8",
    Repair: "#B45309",
    Recycle: "#64748B",
  };

  return (
    <div className="max-w-[1180px] mx-auto" data-testid="recovery-page">
      {/* Title */}
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-3" style={{ color: brandBlue }}>
          Recovery &amp; Circularity
        </div>
        <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-[1.15]">
          Every exit becomes value recovered.
        </h1>
        <p className="text-slate-500 mt-3 max-w-2xl text-[15px] leading-7">
          HR-triggered workflow: collect → wipe → grade → decide → close in HAM. Chain of custody at every hop, dollar-value recovered per device.
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
        {summary && (
          <>
            <SummaryCard label="Open recoveries" value={summary.open_recoveries} note="across 4 regions" brandBlue={brandBlue} brandSoft={brandSoft} />
            <SummaryCard label="Blancco wipes today" value={summary.blancco_wipes_today} note="All passing" brandBlue={brandBlue} brandSoft={brandSoft} />
            <SummaryCard label="Ready to resell" value={summary.ready_to_resell_value} note="grades A / B" brandBlue={brandBlue} brandSoft={brandSoft} />
            <SummaryCard label="Recycle-only" value={summary.recycle_only_count} note="grade F devices" brandBlue={brandBlue} brandSoft={brandSoft} />
            <SummaryCard label="Chain of custody" value={`${summary.chain_of_custody_pct}%`} note="Auditable" brandBlue={brandBlue} brandSoft={brandSoft} />
          </>
        )}
      </div>

      {/* 6-step pipeline */}
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-5">The recovery pipeline</div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {steps.map((s) => {
            const Icon = STEP_ICONS[s.id] || Package;
            return (
              <div
                key={s.id}
                className="rounded-xl bg-white p-4"
                style={{ border: `1px solid ${brandSoft}` }}
                data-testid={`recovery-step-${s.id}`}
              >
                <div
                  className="w-9 h-9 rounded-full grid place-items-center text-white text-xs font-semibold mb-3"
                  style={{ backgroundColor: brandBlue }}
                >
                  {s.num}
                </div>
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <Icon size={12} style={{ color: brandBlue }} />
                  {s.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Day {s.day}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active recoveries + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-8">
        {/* List */}
        <div className="rounded-2xl bg-white p-4" style={{ border: `1px solid ${brandSoft}` }}>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">Active recoveries</div>
          <div className="space-y-1">
            {active.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                data-testid={`recovery-item-${r.asset_tag}`}
                className="w-full text-left rounded-lg p-3 transition-colors hover:bg-slate-50"
                style={r.id === selectedId ? { backgroundColor: brandSoft } : {}}
              >
                <div className="text-sm font-semibold text-slate-900 font-mono">{r.asset_tag}</div>
                <div className="text-xs text-slate-500 truncate">{r.employee} · {r.location}</div>
                <div className="text-[11px] mt-1 font-semibold" style={{ color: decisionColor[r.decision.action] || "#64748B" }}>
                  {r.decision.action}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        {selected && (
          <div className="rounded-2xl bg-white p-8" style={{ border: `1px solid ${brandSoft}` }} data-testid="recovery-detail">
            <div className="flex items-start justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
              <div>
                <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-1">
                  Recovery · {selected.id}
                </div>
                <h2 className="text-xl font-semibold text-slate-900">{selected.device_model}</h2>
                <div className="text-sm text-slate-500 mt-0.5">
                  {selected.asset_tag} · {selected.age_months} months old · {selected.location}
                </div>
              </div>
              <div
                className="rounded-lg px-4 py-2 text-right"
                style={{ backgroundColor: brandSoft }}
              >
                <div className="text-[10px] tracking-[0.14em] uppercase font-semibold" style={{ color: brandBlue }}>Recovered</div>
                <div className="text-xl font-semibold" style={{ color: brandBlue }}>${selected.recovered_value.toFixed(0)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DetailRow label="Employee" value={selected.employee} />
              <DetailRow label="HR trigger" value={`Exit event · ${selected.hr_trigger_date}`} />
              <DetailRow label="Collect" value={`${selected.collect.carrier} · ${selected.collect.status} · ${selected.collect.date}`} />
              <DetailRow label="Wipe" value={`${selected.wipe.tool} · ${selected.wipe.status} · cert #${selected.wipe.cert}`} />
              <DetailRow label="Grade" value={`${selected.grading.status} · ${selected.grading.note}`} />
              <DetailRow label="Decision" value={selected.decision.action} accent={decisionColor[selected.decision.action]} />
              <DetailRow label="HAM status" value={`${selected.ham.status} · ${selected.ham.note}`} />
              <DetailRow label="Chain of custody" value={`Complete · ${selected.chain_of_custody_events} events logged`} />
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-2">Agent decision logic</div>
              <p className="text-sm text-slate-700 leading-6">
                The Recovery &amp; Circularity Agent picked{" "}
                <span className="font-semibold" style={{ color: decisionColor[selected.decision.action] }}>
                  {selected.decision.action.toUpperCase()}
                </span>{" "}
                — {selected.decision.reason}.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, note, brandBlue, brandSoft }) {
  return (
    <div className="rounded-2xl bg-white p-5" style={{ border: `1px solid ${brandSoft}` }}>
      <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-2">{label}</div>
      <div className="text-2xl font-semibold" style={{ color: brandBlue }}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{note}</div>
    </div>
  );
}

function DetailRow({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-medium" style={accent ? { color: accent } : { color: "#0F172A" }}>{value}</div>
    </div>
  );
}
